import * as Sentry from '@sentry/react-native';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { usePostHog } from 'posthog-js/react';
import env from '../utils/environment';
import logger from '../utils/logger';
import supabase from '../utils/supabaseClient';
import { useAuth } from './AuthContext';
import { resolveTrialUnlockEligible } from '../hooks/useTrialUnlockEligibility';

// Tier type definition
export const TIERS = {
  FREE: 'Free',
  PRO: 'Pro',
  PREMIUM: 'Premium',
  ADMIN: 'Admin',
};

// Tier hierarchy for access control
const TIER_HIERARCHY = {
  [TIERS.FREE]: 0,
  [TIERS.PRO]: 1,
  [TIERS.PREMIUM]: 2,
  [TIERS.ADMIN]: 3,
};

// Subscription statuses that grant Pro access unconditionally.
// Step 8 (two-phase trial): `trial_carded` (card-on-file, pre day-7 charge)
// grants full Pro access, mirroring backend hasProAccess. It is NOT a Trial
// bucket here — TierContext's access logic has no `trial_active`-specific
// Trial branch (that distinction lives only in the backend
// computeEffectiveTier), so a carded user reads as Pro everywhere.
const PRO_ACCESS_STATUSES = ['trial_active', 'trial_carded', 'grandfather_active', 'active'];
// Subscription statuses that grant Pro access during grace period
const GRACE_PERIOD_STATUSES = ['cancelled', 'past_due', 'paused'];

// Create context with default shape
export const TierContext = createContext({
  tier: TIERS.FREE,
  subscriptionStatus: 'none',
  subscriptionId: null,
  planInterval: null,
  wasGrandfatherConversion: false,
  proExpiresAt: null,
  realityCheckShownAt: null,
  dormancyConfirmedAt: null,
  trialSummaryStats: null,
  loading: true,
  hasProAccess: !env.isPaywallEnabled,
  isPro: !env.isPaywallEnabled,
  isPremium: false,
  isAdmin: false,
  trialUnlockEligible: false,
  canAccess: () => false,
  refetch: async () => {},
});

/**
 * TierProvider - Context provider component
 * Manages tier, subscription status, and Pro access state with Realtime updates.
 */
export const TierProvider = ({ children }) => {
  const { user } = useAuth();
  const posthog = usePostHog();
  const [tier, setTier] = useState(TIERS.FREE);
  const [subscriptionStatus, setSubscriptionStatus] = useState('none');
  const [proExpiresAt, setProExpiresAt] = useState(null);
  const [realityCheckShownAt, setRealityCheckShownAt] = useState(null);
  const [dormancyConfirmedAt, setDormancyConfirmedAt] = useState(null);
  // 2026-05-26 trial pivot: /pricing renders TrialRecapPanel inline when this
  // jsonb has at least one non-generic branch + realityCheckShownAt is null.
  const [trialSummaryStats, setTrialSummaryStats] = useState(null);
  // D-025: trial + grandfather window timestamps so countdown chip can render.
  const [trialStartedAt, setTrialStartedAt] = useState(null);
  const [trialCohortDays, setTrialCohortDays] = useState(null);
  const [grandfatherStartedAt, setGrandfatherStartedAt] = useState(null);
  const [grandfatherExpiresAt, setGrandfatherExpiresAt] = useState(null);
  // D-010: surface subscription_id so UpgradeModal can hide CBP for users
  // with an active recurring sub (subscription_id != null) vs an active CBP
  // (subscription_id == null but pro_expires_at > now).
  const [subscriptionId, setSubscriptionId] = useState(null);
  // D-101 mirror: 'monthly' | 'annual' | null. Used by /pricing to render
  // "Keep current plan" on the user's matching tile and "Switch to X" on the
  // other one. Null for Free, CBP, and trial users.
  const [planInterval, setPlanInterval] = useState(null);
  // D-030: past-due transaction id captured by paddle-webhook's
  // transaction.payment_failed handler. Used by PastDueBanner to open an
  // inline Paddle.Checkout overlay for card update + retry.
  const [pastDueTransactionId, setPastDueTransactionId] = useState(null);
  // Server-set marker that this user was a grandfather cohort member who
  // converted to a paid subscription (set by paddle-webhook on first
  // grandfather_active → active transition). Used by the discount resolver to
  // keep applying Founder's Rate pricing on /pricing + UpgradeModal after the
  // status flips off `grandfather_active`. Stays true permanently.
  const [wasGrandfatherConversion, setWasGrandfatherConversion] = useState(false);
  const [loading, setLoading] = useState(true);
  const [forceTierFree, setForceTierFree] = useState(false);

  // Step 3: App-wide upgrade modal bus
  // Shape: { source: string, onDismiss?: () => void } | null
  const [upgradeModal, setUpgradeModal] = useState(null);
  // Ref mirror so dismissUpgradeModal can read the latest callback without
  // being re-created when upgradeModal changes (avoids stale-closure bugs).
  const upgradeModalRef = useRef(null);

  // D-064 + D-077: welcome-toast transition detection.
  // Tracks the last known subscription_status so Realtime UPDATEs can detect
  // prev → new transitions without a new DB column.
  // Seeded from fetchTier result (see below) so first-load never false-fires.
  const prevSubscriptionStatusRef = useRef(null);
  // D-077: pending-transitions buffer + debounce timer for Realtime burst coalescing.
  const pendingTransitionsRef = useRef([]);
  const toastDebounceTimerRef = useRef(null);
  // D-027: one-shot welcome toast state. Shape: { branch: string } | null.
  // Populated when D-064 matrix says fire; component renders at app root.
  const [proActivationToast, setProActivationToast] = useState(null);
  // D-026: mid-trial aha toast state. Shape: { trigger: string } | null.
  // Populated by triggerAhaToast() when the server RPC confirms fire.
  const [ahaToast, setAhaToast] = useState(null);
  // D-026: one-toast-per-session cap enforced in-memory.
  const ahaSessionFiredRef = useRef(false);
  useEffect(() => {
    upgradeModalRef.current = upgradeModal;
  }, [upgradeModal]);

  const triggerUpgradeModal = useCallback(({ source, onDismiss, preselectedPricingOption, coupon, belowMarketDelta } = {}) => {
    // belowMarketDelta (item #3): the tapped locked listing's own below-market
    // delta (£), surfaced in the deferred-trial card-entry modal. Optional; null
    // for taps with no price_vs_market signal.
    setUpgradeModal({ source, onDismiss, preselectedPricingOption, coupon, belowMarketDelta });
  }, []);

  const dismissUpgradeModal = useCallback(() => {
    const current = upgradeModalRef.current;
    try {
      current?.onDismiss?.();
    } catch (err) {
      logger.error('[TierContext] upgradeModal.onDismiss threw:', err);
    }
    setUpgradeModal(null);
  }, []);

  // Step 3: Free user's currently active model_id. Source of truth for
  // ProLock taps, dormant cards, FeedListingCard match badge gating, and
  // cap intercepts. Dashboard sets this on mount; clears on unmount.
  const [activeModelId, setActiveModelId] = useState(null);

  // Read ?force_tier=free from URL on mount (dev/internal testing override)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('force_tier') === 'free') {
        const isDev = typeof __DEV__ !== 'undefined' && __DEV__;
        if (isDev) {
          logger.log('[TierContext] force_tier=free activated (dev mode)');
          setForceTierFree(true);
        }
        // PostHog internal-users check is deferred to when posthog is loaded
      }
    } catch {
      // SSR or window not available
    }
  }, []);

  /**
   * Fetches current tier and subscription state from database
   */
  const fetchTier = useCallback(async () => {
    if (!user) {
      setTier(TIERS.FREE);
      setSubscriptionStatus('none');
      setProExpiresAt(null);
      setRealityCheckShownAt(null);
      setDormancyConfirmedAt(null);
      setTrialSummaryStats(null);
      setLoading(false);
      return;
    }

    try {
      logger.log('[TierContext] Fetching tier for user:', user.id);

      const { data, error } = await supabase
        .from('users')
        .select('tier, subscription_status, pro_expires_at, reality_check_shown_at, dormancy_confirmed_at, trial_started_at, trial_cohort_days, grandfather_started_at, grandfather_expires_at, subscription_id, past_due_transaction_id, plan_interval, was_grandfather_conversion, trial_summary_stats')
        .eq('id', user.id)
        .single();

      if (error) {
        logger.error('[TierContext] Failed to fetch tier:', error);
        throw error;
      }

      if (data) {
        setTier(data.tier || TIERS.FREE);
        setSubscriptionStatus(data.subscription_status || 'none');
        setProExpiresAt(data.pro_expires_at || null);
        setRealityCheckShownAt(data.reality_check_shown_at || null);
        setDormancyConfirmedAt(data.dormancy_confirmed_at || null);
        setTrialStartedAt(data.trial_started_at || null);
        setTrialCohortDays(data.trial_cohort_days || null);
        setGrandfatherStartedAt(data.grandfather_started_at || null);
        setGrandfatherExpiresAt(data.grandfather_expires_at || null);
        setSubscriptionId(data.subscription_id || null);
        setPastDueTransactionId(data.past_due_transaction_id || null);
        setPlanInterval(data.plan_interval || null);
        setWasGrandfatherConversion(data.was_grandfather_conversion === true);
        setTrialSummaryStats(data.trial_summary_stats || null);
        // D-064: seed prev_status ref from initial fetch so the first Realtime
        // UPDATE has a valid baseline and does not false-fire.
        prevSubscriptionStatusRef.current = data.subscription_status || 'none';
        logger.log('[TierContext] Fetched:', {
          tier: data.tier,
          subscriptionStatus: data.subscription_status,
          proExpiresAt: data.pro_expires_at,
        });
      } else {
        setTier(TIERS.FREE);
        setSubscriptionStatus('none');
        setProExpiresAt(null);
        setRealityCheckShownAt(null);
        setDormancyConfirmedAt(null);
        setTrialStartedAt(null);
        setTrialCohortDays(null);
        setGrandfatherStartedAt(null);
        setGrandfatherExpiresAt(null);
        setSubscriptionId(null);
        setPastDueTransactionId(null);
        setPlanInterval(null);
        setWasGrandfatherConversion(false);
        setTrialSummaryStats(null);
        prevSubscriptionStatusRef.current = 'none';
      }
    } catch (error) {
      logger.error('[TierContext] Error fetching tier:', error);
      Sentry.captureException(error, {
        tags: { component: 'TierContext' },
        extra: { userId: user?.id },
      });
      setTier(TIERS.FREE);
      setSubscriptionStatus('none');
      setProExpiresAt(null);
      setRealityCheckShownAt(null);
      setDormancyConfirmedAt(null);
      setTrialSummaryStats(null);
      prevSubscriptionStatusRef.current = 'none';
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  /**
   * D-064 matrix: determines whether a prev_status → active transition should
   * fire the welcome-toast (pro_activation_detected PostHog event placeholder).
   * Returns true = fire, false = suppress.
   *
   * | prev_status                              | fire? |
   * | trial_active / trial_expired             | yes   |
   * | grandfather_active / grandfather_expired | yes   |
   * | cancelled                                | yes   |
   * | none                                     | yes   |
   * | paused                                   | no    | journey #8 owns moment
   * | past_due                                 | no    | passive card retry
   * | any other (active → active, etc.)        | no    |
   */
  const shouldFireWelcomeToast = useCallback((prevStatus, newStatus) => {
    if (newStatus !== 'active') return false;
    const FIRE_SET = new Set(['trial_active', 'trial_expired', 'grandfather_active', 'grandfather_expired', 'cancelled', 'none']);
    return FIRE_SET.has(prevStatus);
  }, []);

  /**
   * D-027: map prev_status into one of the five copy branches for the welcome
   * toast. CBP is a future branch; for now it's unreachable (no CBP product code
   * path exists) but included so the UI doesn't crash if a CBP status lands.
   */
  const resolveProActivationBranch = (prevStatus) => {
    switch (prevStatus) {
      case 'trial_active':
      case 'trial_expired':
        return 'trial_convert';
      case 'grandfather_active':
      case 'grandfather_expired':
        return 'grandfather_convert';
      case 'cancelled':
        return 'lapsed_convert';
      case 'none':
        return 'first_time';
      default:
        return 'first_time';
    }
  };

  /**
   * D-064 + D-077 + D-027: evaluate a coalesced transition and both emit the
   * PostHog event + open the welcome toast when the D-064 matrix says fire.
   * localStorage dedupe (per user) avoids re-fire on reload; cross-device
   * dedupe is an accepted v1 limitation (D-064 note).
   */
  const evaluateAndEmitTransition = useCallback((firstPrev, lastNew) => {
    if (!shouldFireWelcomeToast(firstPrev, lastNew)) {
      logger.log('[TierContext] D-064 matrix: suppress welcome toast', { firstPrev, lastNew });
      return;
    }
    logger.log('[TierContext] D-064 matrix: fire pro_activation_detected', { firstPrev, lastNew });
    try {
      posthog?.capture('pro_activation_detected', {
        prev_status: firstPrev,
        new_status: lastNew,
        user_id: user?.id,
      });
    } catch (err) {
      logger.error('[TierContext] Failed to emit pro_activation_detected:', err);
    }

    // D-027: dedupe + toast open.
    const dedupeKey = user?.id ? `pro_activation_welcome_shown_v1:${user.id}` : null;
    try {
      if (dedupeKey && typeof window !== 'undefined' && window.localStorage?.getItem(dedupeKey)) {
        logger.log('[TierContext] D-027 welcome toast already shown for user, skipping');
        return;
      }
    } catch {
      // SSR or storage disabled — fall through and show toast (fail-open).
    }

    const branch = resolveProActivationBranch(firstPrev);
    setProActivationToast({ branch });
    try {
      if (dedupeKey && typeof window !== 'undefined') {
        window.localStorage?.setItem(dedupeKey, String(Date.now()));
      }
    } catch {
      // non-fatal
    }

    // Belt-and-suspenders post-purchase redirect. The primary redirect path
    // lives in PricingSection.onSuccess (PricingSection.js:341), driven by
    // Paddle's `checkout.completed` event. If that event fails to reach the
    // handler (rare but observed 2026-05-25: Pro Monthly purchase landed
    // server-side, Paddle's built-in success screen stayed open, redirect
    // never fired), the Realtime UPDATE on `users.subscription_status` still
    // triggers this transition path. Force-navigate off /pricing so the
    // welcome toast lands on /home in context.
    if (typeof window !== 'undefined' && window.location?.pathname === '/pricing') {
      try {
        window.location.assign('/home');
      } catch (err) {
        logger.warn('[TierContext] post-activation /pricing redirect failed:', err);
      }
    }
  }, [shouldFireWelcomeToast, posthog, user?.id]);

  const dismissProActivationToast = useCallback(() => {
    setProActivationToast(null);
  }, []);

  /**
   * Step 8 (two-phase trial) reveal handler. On the Realtime
   * `trial_active -> trial_carded` transition (the user added a card and now
   * has full Pro before any charge), fire a one-time unlock toast + flag the
   * feed unlock animation. localStorage dedupe (per user) so a reload does not
   * replay it. This is SEPARATE from the D-064 welcome-toast matrix: the day-7
   * `trial_carded -> active` transition stays SILENT (it is not in FIRE_SET and
   * is not handled here), per design E3 / R6.5.
   */
  const evaluateCardedReveal = useCallback((prevStatus, newStatus) => {
    if (!(prevStatus === 'trial_active' && newStatus === 'trial_carded')) return;

    const dedupeKey = user?.id ? `trial_carded_unlock_shown_v1:${user.id}` : null;
    try {
      if (dedupeKey && typeof window !== 'undefined' && window.localStorage?.getItem(dedupeKey)) {
        logger.log('[TierContext] Step 8 unlock reveal already shown for user, skipping');
        return;
      }
    } catch {
      // SSR or storage disabled: fall through and show (fail-open).
    }

    logger.log('[TierContext] Step 8: trial_active -> trial_carded reveal');
    try {
      posthog?.capture('trial_carded_reveal', {
        prev_status: prevStatus,
        new_status: newStatus,
        user_id: user?.id,
      });
    } catch (err) {
      logger.error('[TierContext] Failed to emit trial_carded_reveal:', err);
    }

    // Drive the feed unlock animation (bounded ~600-900ms, completes before
    // items are interactable). Consumers read `feedUnlockAt` and run a one-shot
    // Reanimated/Animated reveal. Cleared after the bound so re-mounts do not
    // replay it.
    setFeedUnlockAt(Date.now());

    setProActivationToast({ branch: 'trial_carded_unlock' });
    try {
      if (dedupeKey && typeof window !== 'undefined') {
        window.localStorage?.setItem(dedupeKey, String(Date.now()));
      }
    } catch {
      // non-fatal
    }
  }, [posthog, user?.id]);

  const evaluateCardedRevealRef = useRef(evaluateCardedReveal);
  useEffect(() => {
    evaluateCardedRevealRef.current = evaluateCardedReveal;
  }, [evaluateCardedReveal]);

  // Step 8: timestamp of the most recent carded reveal so the feed can run a
  // bounded one-shot unlock animation. Auto-clears after FEED_UNLOCK_MS.
  const FEED_UNLOCK_MS = 900;
  const [feedUnlockAt, setFeedUnlockAt] = useState(null);
  useEffect(() => {
    if (!feedUnlockAt) return;
    const t = setTimeout(() => setFeedUnlockAt(null), FEED_UNLOCK_MS);
    return () => clearTimeout(t);
  }, [feedUnlockAt]);

  /**
   * D-026: trigger a mid-trial aha toast. Calls the server RPC which applies
   * all throttle rules (48h windows, cohort cap, idempotence, trial_active
   * gate). Client additionally enforces the one-toast-per-session cap in-ref
   * so we don't double-fire when multiple callsites trip in the same session.
   */
  const dismissAhaToast = useCallback(() => {
    setAhaToast(null);
  }, []);

  // D-026 listings_viewed_3 tracking: per-session set of distinct listing IDs
  // the user has opened. Resets on TierProvider unmount (new session).
  const viewedListingsRef = useRef(new Set());

  /**
   * D-026 listings_viewed_3 trip-wire. Call on listing-detail view.
   * Tracks distinct listing IDs client-side; at 3, fires the trigger.
   */
  const onListingViewed = useCallback((listingId) => {
    if (subscriptionStatus !== 'trial_active') return;
    if (!listingId) return;
    viewedListingsRef.current.add(listingId);
    if (viewedListingsRef.current.size === 3) {
      triggerAhaToastRef.current?.('listings_viewed_3');
    }
  }, [subscriptionStatus]);

  /**
   * D-026 dealer_click_1 trip-wire. Call on first dealer-site click-through.
   * Server RPC enforces idempotence, so firing on every click is safe.
   */
  const onDealerClicked = useCallback(() => {
    if (subscriptionStatus !== 'trial_active') return;
    triggerAhaToastRef.current?.('dealer_click_1');
  }, [subscriptionStatus]);

  /**
   * D-026 neg_2 trip-wire. Call after negotiation creation. Queries user's
   * negotiation count; fires when it crosses to exactly 2.
   */
  const onNegotiationStarted = useCallback(async () => {
    if (subscriptionStatus !== 'trial_active') return;
    if (!user?.id) return;
    try {
      const { count, error } = await supabase
        .from('negotiations')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', user.id);
      if (error) return;
      if (count === 2) {
        triggerAhaToastRef.current?.('neg_2');
      }
    } catch {
      // non-fatal
    }
  }, [subscriptionStatus, user?.id]);

  /**
   * D-026 saves_2 trip-wire. Call from save-listing handlers. Queries the
   * user's saved-count; when it crosses to exactly 2, fires the trigger.
   * Guarded by subscription_status so non-trial users don't pay the query.
   */
  const onListingSaved = useCallback(async () => {
    if (subscriptionStatus !== 'trial_active') return;
    if (!user?.id) return;
    try {
      const { count, error } = await supabase
        .from('user_listing_feeds')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .not('saved_at', 'is', null);
      if (error) return;
      if (count === 2) {
        triggerAhaToastRef.current?.('saves_2');
      }
    } catch {
      // non-fatal
    }
  }, [subscriptionStatus, user?.id]);
  // Ref mirror so onListingSaved doesn't need triggerAhaToast in its deps
  // (which would churn the callback identity on every posthog change).
  const triggerAhaToastRef = useRef(null);

  const triggerAhaToast = useCallback(async (triggerKey) => {
    if (ahaSessionFiredRef.current) return;
    if (!user?.id) return;
    try {
      const { data, error } = await supabase.rpc('mark_aha_seen', { p_trigger_key: triggerKey });
      if (error) {
        logger.warn('[TierContext] mark_aha_seen RPC error', error);
        return;
      }
      if (data === true) {
        ahaSessionFiredRef.current = true;
        setAhaToast({ trigger: triggerKey });
        try {
          posthog?.capture('trial_aha_toast_shown', {
            trigger: triggerKey,
            user_id: user.id,
          });
        } catch (err) {
          logger.warn('[TierContext] posthog capture failed', err);
        }
      }
    } catch (err) {
      logger.warn('[TierContext] triggerAhaToast threw', err);
    }
  }, [user?.id, posthog]);

  // Wire the ref after triggerAhaToast is defined so onListingSaved can call it.
  useEffect(() => {
    triggerAhaToastRef.current = triggerAhaToast;
  }, [triggerAhaToast]);

  // Stable ref for evaluateAndEmitTransition so the Realtime useEffect does not
  // need it in its deps (posthog's identity is unstable when consumers wrap
  // the provider in a new React tree, which would otherwise cause the
  // useEffect cleanup to run mid-debounce-window and drop pending transitions).
  const evaluateAndEmitTransitionRef = useRef(evaluateAndEmitTransition);
  useEffect(() => {
    evaluateAndEmitTransitionRef.current = evaluateAndEmitTransition;
  }, [evaluateAndEmitTransition]);

  /**
   * Set up tier fetching and realtime subscription
   */
  useEffect(() => {
    if (!env.isPaywallEnabled) {
      logger.log('[TierContext] Paywall disabled via env - all users have Pro access');
    }

    fetchTier();

    if (!user) return;

    logger.log('[TierContext] Setting up realtime subscription for user:', user.id);

    const channel = supabase
      .channel(`user-tier-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'users',
          filter: `id=eq.${user.id}`,
        },
        (payload) => {
          logger.log('[TierContext] Realtime update received');
          const newRow = payload.new;

          // Unconditional setters: React batches these into a single re-render.
          // Avoids stale-closure equality guards that miss subscription-only changes.
          if (newRow?.tier) setTier(newRow.tier);
          if (newRow?.subscription_status !== undefined) setSubscriptionStatus(newRow.subscription_status);
          if (newRow?.pro_expires_at !== undefined) setProExpiresAt(newRow.pro_expires_at);
          if (newRow?.reality_check_shown_at !== undefined) setRealityCheckShownAt(newRow.reality_check_shown_at);
          if (newRow?.dormancy_confirmed_at !== undefined) setDormancyConfirmedAt(newRow.dormancy_confirmed_at);
          if (newRow?.subscription_id !== undefined) setSubscriptionId(newRow.subscription_id ?? null);
          if (newRow?.past_due_transaction_id !== undefined) setPastDueTransactionId(newRow.past_due_transaction_id ?? null);
          if (newRow?.plan_interval !== undefined) setPlanInterval(newRow.plan_interval ?? null);
          if (newRow?.was_grandfather_conversion !== undefined) {
            setWasGrandfatherConversion(newRow.was_grandfather_conversion === true);
          }
          if (newRow?.trial_summary_stats !== undefined) setTrialSummaryStats(newRow.trial_summary_stats ?? null);

          // D-064 + D-077: welcome-toast transition detection with 500ms debounce.
          // Each UPDATE pushes {prev, new} into the pending buffer, resets the
          // timer. On timer fire, first.prev + last.new are evaluated against the
          // D-064 matrix and a single PostHog event is emitted if matrix says fire.
          // This coalesces Paddle webhook bursts (e.g. past_due→cancelled→active)
          // into a single deterministic event.
          if (newRow?.subscription_status !== undefined) {
            const prevStatus = prevSubscriptionStatusRef.current;
            const newStatus = newRow.subscription_status;
            prevSubscriptionStatusRef.current = newStatus;

            // Step 8: fire the card-add reveal (unlock toast + feed animation)
            // immediately on the trial_active -> trial_carded transition. This is
            // a distinct, non-debounced signal from the D-064 welcome-toast matrix
            // (which only fires for transitions INTO 'active'). The day-7
            // trial_carded -> active transition is deliberately not handled here.
            evaluateCardedRevealRef.current?.(prevStatus, newStatus);

            pendingTransitionsRef.current.push({ prev: prevStatus, new: newStatus });

            if (toastDebounceTimerRef.current) {
              clearTimeout(toastDebounceTimerRef.current);
            }
            toastDebounceTimerRef.current = setTimeout(() => {
              const transitions = pendingTransitionsRef.current;
              pendingTransitionsRef.current = [];
              toastDebounceTimerRef.current = null;

              if (transitions.length === 0) return;
              const firstPrev = transitions[0].prev;
              const lastNew = transitions[transitions.length - 1].new;
              evaluateAndEmitTransitionRef.current(firstPrev, lastNew);
            }, 500);
          }

          Sentry.addBreadcrumb({
            category: 'tier',
            message: 'User row updated via realtime',
            data: {
              tier: newRow?.tier,
              subscriptionStatus: newRow?.subscription_status,
              userId: user.id,
            },
            level: 'info',
          });
        }
      )
      .subscribe();

    return () => {
      logger.log('[TierContext] Cleaning up realtime subscription');
      supabase.removeChannel(channel);
      // D-077: cancel any pending debounce timer on cleanup to avoid firing
      // after unmount or identity change.
      if (toastDebounceTimerRef.current) {
        clearTimeout(toastDebounceTimerRef.current);
        toastDebounceTimerRef.current = null;
      }
      pendingTransitionsRef.current = [];
    };
  }, [user?.id]);

  /**
   * Checks if user has access to a required tier
   */
  const canAccess = useCallback((requiredTier) => {
    if (!env.isPaywallEnabled && (TIER_HIERARCHY[requiredTier] ?? 0) <= TIER_HIERARCHY[TIERS.PRO]) {
      return true;
    }
    const userLevel = TIER_HIERARCHY[tier] ?? 0;
    const requiredLevel = TIER_HIERARCHY[requiredTier] ?? 0;
    return userLevel >= requiredLevel;
  }, [tier]);

  // hasProAccess: single source of truth for Pro-level gating
  const hasProAccess = useMemo(() => {
    // Kill switch: everyone is Pro
    if (!env.isPaywallEnabled) return true;

    // Dev testing override
    if (forceTierFree) return false;

    // Tier-based (Admin/Lifetime grants)
    const tierLevel = TIER_HIERARCHY[tier] ?? 0;
    if (tierLevel >= TIER_HIERARCHY[TIERS.PRO]) return true;

    // Subscription-based
    if (PRO_ACCESS_STATUSES.includes(subscriptionStatus)) return true;

    // Grace period (cancelled/past_due/paused with remaining access)
    if (
      GRACE_PERIOD_STATUSES.includes(subscriptionStatus) &&
      proExpiresAt &&
      new Date(proExpiresAt) > new Date()
    ) {
      return true;
    }

    return false;
  }, [tier, subscriptionStatus, proExpiresAt, forceTierFree]);

  const isPro = hasProAccess;
  const isPremium = canAccess(TIERS.PREMIUM);
  const isAdmin = canAccess(TIERS.ADMIN);

  // Step 8: the two-phase trial is the default + only flow (no experiment).
  // True for an in-flight cardless trial (trial_active). Drives the "Unlock"
  // feed CTA framing (R8.5). Re-derives from the live subscriptionStatus so it
  // flips off the moment the user leaves trial_active (e.g. becomes
  // trial_carded).
  const trialUnlockEligible = resolveTrialUnlockEligible({ subscriptionStatus });

  // Memoize so that stable values (hasProAccess, activeModelId, callbacks) don't
  // cause all context consumers to re-render when unrelated state (e.g. upgradeModal)
  // changes. Each dep change still propagates; this just collapses needless re-creates.
  const value = useMemo(
    () => ({
      tier,
      subscriptionStatus,
      subscriptionId,
      planInterval,
      pastDueTransactionId,
      proExpiresAt,
      realityCheckShownAt,
      dormancyConfirmedAt,
      trialSummaryStats,
      trialStartedAt,
      trialCohortDays,
      grandfatherStartedAt,
      grandfatherExpiresAt,
      wasGrandfatherConversion,
      loading,
      hasProAccess,
      isPro,
      isPremium,
      isAdmin,
      // Step 8: deferred-trial eligibility (feed CTA framing)
      trialUnlockEligible,
      canAccess,
      refetch: fetchTier,
      // Step 3 additions
      upgradeModal,
      triggerUpgradeModal,
      dismissUpgradeModal,
      activeModelId,
      setActiveModelId,
      // D-027 welcome toast bus
      proActivationToast,
      dismissProActivationToast,
      // Step 8: feed unlock animation trigger (carded reveal). A timestamp that
      // is non-null for ~900ms after the trial_active -> trial_carded reveal.
      feedUnlockAt,
      // D-026 mid-trial aha toast bus
      ahaToast,
      dismissAhaToast,
      triggerAhaToast,
      onListingSaved,
      onListingViewed,
      onDealerClicked,
      onNegotiationStarted,
    }),
    [
      tier, subscriptionStatus, subscriptionId, planInterval, pastDueTransactionId, proExpiresAt, realityCheckShownAt, dormancyConfirmedAt, trialSummaryStats,
      trialStartedAt, trialCohortDays, grandfatherStartedAt, grandfatherExpiresAt, wasGrandfatherConversion,
      loading,
      hasProAccess, isPro, isPremium, isAdmin, trialUnlockEligible, canAccess, fetchTier,
      upgradeModal, triggerUpgradeModal, dismissUpgradeModal,
      activeModelId, setActiveModelId,
      proActivationToast, dismissProActivationToast, feedUnlockAt,
      ahaToast, dismissAhaToast, triggerAhaToast, onListingSaved,
      onListingViewed, onDealerClicked, onNegotiationStarted,
    ]
  );

  return <TierContext.Provider value={value}>{children}</TierContext.Provider>;
};

/**
 * useTier hook - Consumes tier context
 */
export const useTier = () => {
  const context = useContext(TierContext);
  if (!context) {
    throw new Error('useTier must be used within TierProvider');
  }
  return context;
};

export default TierContext;
